import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Banner } from "../Banner";
import { BottomSheet } from "../BottomSheet";
import { Button } from "../Button";
import { Input } from "../Input";
import { useChannels } from "@/hooks/useApp";
import { api } from "@/lib/api";
import type { Channel } from "@zapir/shared";
import { ZapirApiError, toApiError } from "@/lib/errors";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Iniciar conversa a partir de um canal + telefone. Saiu da lista de conversas
 * porque é outro assunto: a lista só lê, isto escreve.
 */
export function NewConversationSheet({ open, onClose }: Props) {
  const channels = useChannels();
  const activeChannels = (channels.data ?? []).filter((c) => c.status === "active");
  return (
    <BottomSheet open={open} onClose={onClose} title="Nova conversa" label="Nova conversa">
      {activeChannels.length === 0 ? (
        <p className="text-[13px] muted">Nenhum canal ativo. Conecte um canal em Ajustes &gt; Canais antes de iniciar uma conversa.</p>
      ) : (
        // O formulário é um componente à parte de propósito: o `BottomSheet`
        // desmonta os filhos ao fechar, então cada abertura já começa em
        // branco com o primeiro canal ativo — o mesmo reset que o botão "+"
        // fazia à mão antes.
        <NewConversationForm channels={activeChannels} onClose={onClose} />
      )}
    </BottomSheet>
  );
}

function NewConversationForm({ channels, onClose }: { channels: Channel[]; onClose: () => void }) {
  const navigate = useNavigate();
  const [channelId, setChannelId] = useState(channels[0]?.id ?? "");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<ZapirApiError | null>(null);

  const startConversation = useMutation({
    mutationFn: () =>
      api.startConversation({
        channelId,
        phone: phone.trim(),
        name: name.trim() || undefined,
      }),
    onSuccess: (conversation) => {
      onClose();
      navigate(`/c/${conversation.id}`);
    },
    onError: (err) => setError(toApiError(err)),
  });

  return (
    <>
      {error ? (
        <Banner tone="danger" className="mx-0">
          <div>
            <span className="font-bold">{error.copy}</span>
            <span className="mono block text-[10px] opacity-80">{error.status} · {error.code}</span>
          </div>
        </Banner>
      ) : null}
      <div className="mt-1">
        <span className="label">Canal</span>
        <div className="flex flex-wrap gap-2">
          {channels.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`chip ${channelId === c.id ? "active" : ""}`}
              aria-pressed={channelId === c.id}
              onClick={() => setChannelId(c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <Input
          label="Telefone (E.164)"
          placeholder="+5511999999999"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
        />
      </div>
      <div className="mt-3">
        <Input label="Nome (opcional)" value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="mt-4 flex gap-2">
        <Button className="flex-1" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          className="flex-1"
          disabled={!channelId || !phone.trim() || startConversation.isPending}
          onClick={() => startConversation.mutate()}
        >
          {startConversation.isPending ? "Iniciando…" : "Iniciar conversa"}
        </Button>
      </div>
    </>
  );
}
